import {
  Container,
  IconButton,
  Spacer,
  SvgClose,
  SvgDownload,
  SvgLaunch,
  Text,
} from '@loomhq/lens';
import React, { Component } from 'react';
import CircularProgressbar from 'react-circular-progressbar';
import { connect } from 'react-redux';

import {
  updateAttachmentInputSelectionError,
  updateAttachmentWidgetActive,
  updateCreateAttachmentWithTimestamp,
} from 'actions/attachments';
import { ATTACHMENT_TYPE_LINK } from 'constants/attachments';
import { ATTACHMENT_OPENED } from 'constants/events';
import {
  createLinkAttachmentCreator,
  createUploadAttachmentCreator,
  deleteAttachmentCreator,
  fetchAllAttachmentsCreator,
} from 'creators/attachments';
import * as analytics from 'utilities/analytics';
import DownloadProgress from 'utilities/download-progress';
import { secondsToVideoTS } from 'utilities/time';

import LinkAttachmentForm from './link-attachment-form';
import UploadAttachmentForm from './upload-attachment-form';
import { CALL_OUT_PURPLE, WHITE } from '../../../constants/colors';

import SharePageDropzone from 'components/share-video/share-page-dropzone';
import Button from 'components/ui-components/button';
import Checkbox from 'components/ui-components/checkbox';

import './styles.less';

class AttachmentWidgetItem extends Component {
  state = {
    downloading: false,
    downloadProgress: 0,
    downloadProgressInstance: null,
  };

  render() {
    const { attachment } = this.props;
    const { downloading, downloadProgress } = this.state;

    const attachmentIsLink = attachment.type === ATTACHMENT_TYPE_LINK;
    const attachmentComplete = attachmentIsLink || attachment.upload_complete;

    return (
      <div
        className={`attachment-widget-item ${
          attachmentComplete ? 'complete' : ''
        }`}
      >
        <span className="attachment-name-section">
          <Text hasEllipsis>{attachment.name}</Text>
        </span>
        {!downloading && (
          <React.Fragment>
            <div className="attachment-buttons-section">
              <IconButton
                icon={attachmentIsLink ? <SvgLaunch /> : <SvgDownload />}
                onClick={this.handleOpen}
              />
            </div>
            <div className="attachment-buttons-section">
              <IconButton icon={<SvgClose />} onClick={this.handleDelete} />
            </div>
          </React.Fragment>
        )}
        {downloading && (
          <div className="circular-progress-bar-section">
            <CircularProgressbar
              percentage={downloadProgress}
              strokeWidth={10}
              styles={{
                path: {
                  stroke: CALL_OUT_PURPLE,
                  strokeLinecap: 'round',
                },
                trail: {
                  stroke: WHITE,
                },
              }}
            />
          </div>
        )}
      </div>
    );
  }

  componentWillUnmount() {
    if (this.state.downloadProgressInstance) {
      this.state.downloadProgressInstance.abort();
    }
  }

  handleOpen = () => {
    const {
      attachment: { type, upload_complete, url },
    } = this.props;

    if (type === ATTACHMENT_TYPE_LINK) {
      analytics.track(ATTACHMENT_OPENED, this.getTrackProperties());

      return window.open(url, '_blank');
    }

    if (upload_complete) {
      this.handleDownload();
    }
  };

  handleDelete = e => {
    e.stopPropagation();
    this.props.onDelete(this.props.attachment.id);
  };

  handleDownload = () => {
    if (this.state.downloading) {
      return false;
    }

    const {
      attachment: { download_url: downloadUrl, name: downloadName },
    } = this.props;
    const onProgress = percent => this.setState({ downloadProgress: percent });
    const onError = () => this.resetDownloadState();
    const onComplete = () => {
      analytics.track(ATTACHMENT_OPENED, this.getTrackProperties());
      this.resetDownloadState();
      download.save();
    };
    const download = new DownloadProgress(onProgress, onComplete, onError);

    download.fetch(downloadUrl, downloadName);
    this.setState({ downloading: true, downloadProgressInstance: download });
  };

  resetDownloadState = () => {
    this.setState({
      downloading: false,
      downloadProgress: 0,
      downloadProgressInstance: null,
    });
  };

  getTrackProperties = () => {
    const {
      attachment: {
        type: attachment_type,
        url: attachment_url,
        mime,
        name,
        owner_id,
      },
      currentUserIsOwner,
      videoId,
    } = this.props;

    return {
      video_id: videoId,
      is_video_owner: currentUserIsOwner,
      attachment_type,
      attachment_url,
      mime,
      name,
      owner_id,
    };
  };
}

class AttachmentWidget extends Component {
  render() {
    const {
      attachments,
      createWithTimestamp,
      currentTime,
      currentUserIsOwner,
      numCompleteAttachments,
      inputSelectionError,
      videoId,
      widgetActive,
    } = this.props;

    if (!widgetActive) {
      return (
        <div className="attachment-widget deactivated">
          <Text variant="title">Attach Files</Text>
          <p>
            Attach files from your computer or link with a URL to give your
            viewers more context.
          </p>
          <Button
            buttonType="rp-secondary-3"
            onClicked={this.props.openWidget}
            value="Attach Files"
          />
        </div>
      );
    }

    return (
      <div className="attachment-widget">
        {/* make sure this is rendered first so the rest of the widget lies above it */}
        <SharePageDropzone multiple={true} onDrop={this.handleFiles} />
        <span className="close">
          <IconButton icon={<SvgClose />} onClick={this.handleClose} />
        </span>
        <Text variant="title">Link Resources</Text>
        <LinkAttachmentForm
          createLinkAttachment={this.props.createLinkAttachment}
        />
        <UploadAttachmentForm
          onSelection={this.handleFileSelection}
          error={inputSelectionError}
        />
        <Spacer top={2} bottom={2}>
          <Container backgroundColor="border" height="1px" />
        </Spacer>
        <div className="create-with-timestamp">
          <Checkbox
            isChecked={createWithTimestamp}
            isDisabled={false}
            onEnable={this.handleIncludeTimestamp}
            onDisable={this.handleRemoveTimestamp}
          />
          <span>
            Attach at{' '}
            <span className="time-link">
              {secondsToVideoTS(Math.round(currentTime))}
            </span>
          </span>
        </div>
        <Spacer top={4}>
          {numCompleteAttachments > 0 && (
            <span>
              {numCompleteAttachments}{' '}
              {`attachment${numCompleteAttachments > 1 ? 's' : ''}`}
            </span>
          )}
          {attachments.map((attachment, ind) => (
            <AttachmentWidgetItem
              key={ind}
              attachment={attachment}
              currentUserIsOwner={currentUserIsOwner}
              onDelete={this.props.deleteAttachment}
              videoId={videoId}
            />
          ))}
        </Spacer>
      </div>
    );
  }

  componentDidMount() {
    const { attachmentsFetchedOnce } = this.props;

    if (attachmentsFetchedOnce) {
      return;
    }

    this.props.fetchAllAttachmentsCreator();
  }

  handleClose = () => {
    this.props.updateAttachmentInputSelectionError(null);
    this.props.closeWidget();
  };

  handleIncludeTimestamp = () => {
    this.props.updateIncludeTimestamp(true);
  };

  handleRemoveTimestamp = () => {
    this.props.updateIncludeTimestamp(false);
  };

  handleSumbitLink = () => {
    const { attachmentUrl } = this.state;

    this.props.createLinkAttachment(attachmentUrl);
  };

  handleFileSelection = event => {
    const {
      target: { files },
    } = event;

    if (!files[0]) {
      return;
    }

    this.handleFiles(files);
  };

  handleFiles = files => {
    for (let x = 0; x < files.length; x++) {
      this.props.createUploadAttachment(files[x]);
    }
  };
}

const mapStateToProps = state => {
  const {
    attachments,
    fetched_once: attachmentsFetchedOnce,
    upload_queue: uploadQueue,
  } = state.attachments;
  const {
    current_user_is_owner: currentUserIsOwner,
    video: { id: videoId },
  } = state.share_video;
  const { current_time: currentTime } = state.video_player;

  const {
    createWithTimestamp,
    input_selection_error: inputSelectionError,
    widget_active: widgetActive,
  } = state.attachments;

  let finalAttachments = attachments
    .sort(attachment => attachment.createdAt)
    .filter(
      attachment =>
        attachment.type === ATTACHMENT_TYPE_LINK || attachment.upload_complete
    );

  uploadQueue.forEach(uploadAttachment => {
    if (
      finalAttachments.find(attachment => uploadAttachment.id === attachment.id)
    ) {
      return;
    }

    finalAttachments = [uploadAttachment, ...finalAttachments];
  });

  return {
    attachments: finalAttachments,
    attachmentsFetchedOnce,
    currentUserIsOwner,
    createWithTimestamp,
    currentTime,
    inputSelectionError,
    numCompleteAttachments: finalAttachments.length,
    videoId,
    widgetActive,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    closeWidget: () => dispatch(updateAttachmentWidgetActive(false)),
    createLinkAttachment: url => dispatch(createLinkAttachmentCreator(url)),
    createUploadAttachment: file =>
      dispatch(createUploadAttachmentCreator(file)),
    deleteAttachment: attachmentId =>
      dispatch(deleteAttachmentCreator(attachmentId)),
    fetchAllAttachmentsCreator: () => dispatch(fetchAllAttachmentsCreator()),
    openWidget: () => dispatch(updateAttachmentWidgetActive(true)),
    updateAttachmentInputSelectionError: err =>
      dispatch(updateAttachmentInputSelectionError(err)),
    updateIncludeTimestamp: include =>
      dispatch(updateCreateAttachmentWithTimestamp(include)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AttachmentWidget);
